import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import AddIcon from "@mui/icons-material/Add";
import PageContainer from "../common_components/PageContainer";
import TableToolbar from "../common_components/TableToolbar";
import Pagination from "../common_components/Pagination";
import AppButton from "../common_components/AppButton";
import UserDrawer from "../common_components/UserDrawer";
import "../App.css";
import CommonTable from "../common_components/CommonTable";
import CommonTabs from "../common_components/CommonTabs";
import { ApiCall } from "../helper/axios";
import { useSelector, useDispatch } from "react-redux";
import { selectPermissions, selectToken, updatePermissions } from "../redux/authSlice";
import useToast from "../common_components/Toast/useToast";
import { CircularProgress } from "@mui/material";
import ConfirmActionModal from "../common_components/ConfirmActionModal";
import { getCurrentUserId } from "../helper/commonFunctions";
import reportBuilderService from "../services/reportBuilder.service";
import HoverTooltip from "../common_components/hoverOverlay/HoverTooltip";
import CommonChip from "../common_components/CommonChip";
import { toDesignationList } from "../utils/designationFilters";
const roleClassSequence = [
    "role-badge-1",
    "role-badge-2",
    "role-badge-3",
];
function getInitials(firstName = "", lastName = "") {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    const first = f ? f[0].toUpperCase() : "";
    const last = l ? l[0].toUpperCase() : "";
    return `${first}${last}` || "U";
}
/** "Paid Media AM" -> "PMA", "Search Lead" -> "SL", a single word -> its first two letters ("Admin" -> "AD"). */
function designationInitials(designation = "") {
    const words = designation.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return "";
    if (words.length === 1)
        return words[0].slice(0, 2).toUpperCase();
    return words.map((w) => w[0]).join("").toUpperCase().slice(0, 3);
}
/** Same designation -> same chip colour, wherever it appears. */
function designationColorIndex(designation = "") {
    let hash = 0;
    for (const ch of designation.toLowerCase())
        hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    return hash;
}
function mapUsersFromApi(users = []) {
    return (users || []).map((u) => {
        const firstName = u?.first_name || "";
        const lastName = u?.last_name || "";
        const overrides = u?.overrides || {};
        const hasOverrides = Object.keys(overrides).length > 0;
        return {
            id: u?._id,
            name: `${firstName} ${lastName}`.trim(),
            email: u?.email || "",
            roleName: u?.role_id?.name,
            roleId: u?.role_id?._id || null,
            initials: getInitials(firstName, lastName),
            profilePic: u?.profile_pic || null,
            status: u?.status || "",
            hasOverrides: hasOverrides,
            isInvitationSent: u?.is_invitation_sent || false,
            raw: u,
        };
    });
}
export default function UserList() {
    const toast = useToast();
    const dispatch = useDispatch();
    const token = useSelector(selectToken);
    const selectPermission = useSelector(selectPermissions);
    const [activeTab, setActiveTab] = useState("active");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    const [searchText, setSearchText] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const isInitialMount = useRef(true);
    const [activeCount, setActiveCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [roleOptions, setRoleOptions] = useState([]);
    const [employeePortfolioOptions, setEmployeePortfolioOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [editLoadingUserId, setEditLoadingUserId] = useState(null);
    const [invitationLoadingUserId, setInvitationLoadingUserId] = useState(null);
    const [deleteState, setDeleteState] = useState({
        open: false,
        loading: false,
        payload: null,
    });
    // New state for table features
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    const isFirstLoad = useRef(true);
    const hasPermission = selectPermission?.users?.actions?.includes("Manage Users") ?? false;
    const itemsPerPage = 10;
    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const status = activeTab === "active" ? "active" : "inactive";
            const params = new URLSearchParams();
            params.set("page", String(currentPage));
            params.set("limit", String(itemsPerPage));
            if (searchText)
                params.set("searchText", searchText);
            params.set("status", status);
            const sortByMap = {
                userName: "first_name",
                email: "email",
                role: "designation",
            };
            if (sortConfig?.columnId) {
                params.set("sortBy", sortByMap[sortConfig.columnId] || "first_name");
                params.set("sortOrder", sortConfig.direction || "asc");
            }
            // Add column filters as params
            Object.keys(columnFilters).forEach((columnId) => {
                const value = columnFilters[columnId];
                if (value) {
                    if (columnId === "role") {
                        // Find role name from roleOptions using the role ID
                        const selectedRole = roleOptions.find((role) => role.value === value);
                        if (selectedRole) {
                            params.set("role", selectedRole.label);
                        }
                    }
                    else {
                        const id = columnId === "userName" ? "name" : columnId;
                        params.set(id, value);
                    }
                }
            });
            const res = await ApiCall("GET", `/api/users?${params.toString()}`, "", { Authorization: token });
            if (res && 'data' in res && res?.data?.success) {
                const payload = res.data?.data || {};
                const list = mapUsersFromApi(payload?.users || []);
                setRows(list);
                const pagination = payload?.pagination || {};
                setActiveCount(pagination?.active);
                setInactiveCount(pagination?.inactive);
                setTotalPages(Number(pagination?.totalPages || 1));
            }
            else {
                setRows([]);
            }
        }
        catch (e) {
            setRows([]);
        }
        finally {
            setLoading(false);
            isFirstLoad.current = false;
        }
    }, [activeTab, currentPage, sortConfig, token, itemsPerPage, searchText, columnFilters]);
    // Employee Portfolio dropdown: every active user, from the same endpoint Create Report's User filter uses.
    // Loaded each time the drawer opens, so it is the full list and not just the table page on screen.
    useEffect(() => {
        if (!drawerOpen)
            return;
        let cancelled = false;
        (async () => {
            try {
                const res = await reportBuilderService.listReportCreators(token);
                if (cancelled || !res?.data?.success)
                    return;
                const users = res.data.data || [];
                setEmployeePortfolioOptions(users.map((c) => ({
                    value: c.user_id,
                    label: `${c.name}${c.email ? ` (${c.email})` : ""}`.trim() || c.email,
                    chipColor: "#f9f5ff",
                    textColor: "#6941c6",
                })));
            }
            catch {
                // The dropdown keeps whatever it had.
            }
        })();
        return () => { cancelled = true; };
    }, [drawerOpen, token]);
    // Whoever is being edited never belongs on their own portfolio.
    const portfolioOptionsForDrawer = useMemo(() => employeePortfolioOptions.filter((o) => o.value !== selectedUser?.id), [employeePortfolioOptions, selectedUser?.id]);
    // Trigger API on tab/sort/search/pagination changes
    useEffect(() => {
        if (isFirstLoad.current) {
            fetchUsers();
            return;
        }
        const timer = setTimeout(() => {
            fetchUsers();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchUsers, searchText, columnFilters, currentPage, activeTab, sortConfig]);
    const fetchRoles = useCallback(async () => {
        try {
            if (!token)
                return;
            const res = await ApiCall("GET", "/api/roles", "", { Authorization: token });
            if (res && 'data' in res && 'status' in res && res?.data?.success && res?.status === 200) {
                const rolesArray = Array.isArray(res.data?.data?.roles) ? res.data.data.roles : [];
                const options = (rolesArray || []).map((r) => ({
                    value: r?._id,
                    label: r?.name || "",
                    id: r?._id,
                    permissions: r?.permissions || {},
                })).filter((o) => o.value && o.label);
                setRoleOptions(options);
            }
        }
        catch (e) {
            setRoleOptions([]);
        }
    }, [token]);
    const fetchDesignations = useCallback(async () => {
        try {
            if (!token)
                return;
            const res = await ApiCall("GET", "/api/designations", "", { Authorization: token });
            if (res && 'data' in res && 'status' in res && res?.data?.success && res?.status === 200) {
                const designationsArray = Array.isArray(res.data?.data?.designations) ? res.data.data.designations : [];
                const options = (designationsArray || []).map((d) => ({
                    value: d?.name,
                    label: d?.name
                })).filter((o) => o.value && o.label);
                setDesignationOptions(options);
            }
        }
        catch (e) {
            setDesignationOptions([]);
        }
    }, [token]);
    useEffect(() => {
        if (!token)
            return;
        fetchRoles();
        fetchDesignations();
    }, [token, fetchRoles, fetchDesignations]);
    const handleSearchChange = (text) => {
        setSearchText(text);
    };
    const handleSelectionChange = (newSelected) => {
        setSelectedRows(newSelected);
    };
    const handleColumnVisibilityChange = (newHidden) => {
        setHiddenColumns(newHidden);
    };
    const handleColumnVisibilityClick = (event) => {
        setColumnVisibilityAnchor(event.currentTarget);
    };
    const handleColumnVisibilityClose = () => {
        setColumnVisibilityAnchor(null);
    };
    const handleColumnFilterChange = (columnId, filterValue) => {
        setColumnFilters((prev) => {
            const newFilters = { ...prev };
            if (filterValue) {
                newFilters[columnId] = filterValue;
            }
            else {
                delete newFilters[columnId];
            }
            // if (!filterValue && Object.keys(newFilters).length === 0) {
            //     setShowFilterRow(false);
            //     setActiveFilterColumnId(null);
            // }
            return newFilters;
        });
        setCurrentPage(1);
    };
    const handleColumnOrderChange = (newOrder) => {
        setColumnOrder(newOrder);
    };
    const handleColumnPinningChange = (newPinnedColumns) => {
        setPinnedColumns(newPinnedColumns);
    };
    const handleFilterRowToggle = (payload = null) => {
        if (!payload || payload?.mode === "all") {
            setActiveFilterColumnId(null);
            setShowFilterRow((prev) => (payload?.open !== undefined ? payload.open : !prev));
            return;
        }
        if (payload?.mode === "single" && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    };
    const handleSortChange = (columnId, direction) => {
        setSortConfig({ columnId, direction });
        setCurrentPage(1);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    const handleEdit = async (user) => {
        try {
            setEditLoadingUserId(user.id);
            // Fetch fresh roles to get latest permissions before opening edit drawer
            await fetchRoles();
            const res = await ApiCall("GET", `/api/users/${user.id}`, "", { Authorization: token });
            if (res && 'data' in res && res?.data?.success) {
                const fetchedUser = res.data?.data || {};
                const userData = {
                    id: fetchedUser._id,
                    first_name: fetchedUser.first_name || "",
                    last_name: fetchedUser.last_name || "",
                    email: fetchedUser.email || "",
                    role_id: fetchedUser.role?._id || "",
                    user_type: fetchedUser.user_type || "",
                    status: fetchedUser.status === "active" ? "Active" : "Inactive",
                    location: fetchedUser.location || "",
                    stores: fetchedUser.stores || [],
                    employee_portfolio: fetchedUser.employee_portfolio,
                    clockify_user_id: fetchedUser.clockify_user_id || "",
                    profile_pic: fetchedUser.profile_pic || null,
                    permissions: fetchedUser.permissions || {},
                    overrides: fetchedUser.overrides || {},
                    createdAt: fetchedUser.created_at || null,
                    updatedAt: fetchedUser.updated_at || null,
                    two_step_enabled: fetchedUser.two_step_enabled || fetchedUser.two_step_enabled === false ? JSON.parse(fetchedUser.two_step_enabled) : true,
                    is_meeting_am: fetchedUser.is_meeting_am === true,
                };
                setSelectedUser(userData);
                setDrawerOpen(true);
            }
            else {
                const errorRes = res;
                toast.error(errorRes?.response?.data?.message || "Something went wrong");
            }
        }
        catch (error) {
            toast.error("An unexpected error occurred. Please try again.");
        }
        finally {
            setEditLoadingUserId(null);
        }
    };
    const handleDelete = (row) => {
        setDeleteState({
            open: true,
            loading: false,
            payload: row,
        });
    };
    const confirmDelete = async () => {
        const user = deleteState.payload;
        if (!user)
            return;
        setDeleteState((p) => ({ ...p, loading: true }));
        try {
            const res = await ApiCall("DELETE", `/api/users/${user.id}`, null, { Authorization: token });
            if (res && 'data' in res && res?.data?.success) {
                toast.success(res.data?.message || "User deleted");
                setEmployeePortfolioOptions((prev) => prev.filter((opt) => opt.value !== user.id));
                setDeleteState({ open: false, loading: false, payload: null });
                fetchUsers();
            }
            else {
                const errorRes = res;
                toast.error(errorRes?.response?.data?.message || "Delete failed");
                setDeleteState((p) => ({ ...p, loading: false }));
            }
        }
        catch (error) {
            toast.error("An unexpected error occurred. Please try again.");
            setDeleteState((p) => ({ ...p, loading: false }));
        }
    };
    const handleSendInvitation = async (user) => {
        try {
            setInvitationLoadingUserId(user.id);
            const res = await ApiCall("POST", "/api/users/send-invitation", {
                userId: user.id,
                email: user.email
            }, { Authorization: token });
            if (res && 'data' in res && res?.data?.success) {
                toast.success(res.data?.message || "Invitation sent successfully");
                fetchUsers(); // Refresh the list to update the icon color
            }
            else {
                const errorRes = res;
                toast.error(errorRes?.response?.data?.message || "Failed to send invitation");
            }
        }
        catch (error) {
            toast.error("An unexpected error occurred. Please try again.");
        }
        finally {
            setInvitationLoadingUserId(null);
        }
    };
    const handleAddNewUser = () => {
        setSelectedUser(null);
        setDrawerOpen(true);
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedUser(null);
    };
    const handleOverrideSave = (overrides, finalPermissions) => {
        // Update the row in UserList to show icon when override is saved
        if (selectedUser) {
            const hasOverridesData = Object.keys(overrides).length > 0;
            // Check if we are editing the CURRENT user
            const currentUserId = getCurrentUserId();
            if (currentUserId === selectedUser.id && finalPermissions) {
                dispatch(updatePermissions({ permissions: finalPermissions, overrides }));
            }
            // Update selectedUser
            setSelectedUser((prev) => ({
                ...prev,
                overrides: overrides
            }));
            // Update the row in rows array
            setRows((prevRows) => prevRows.map((row) => row.id === selectedUser.id
                ? { ...row, hasOverrides: hasOverridesData }
                : row));
        }
    };
    const handleSaveUser = async (values) => {
        try {
            const formData = new FormData();
            formData.append("first_name", values?.firstName || "");
            formData.append("last_name", values?.lastName || "");
            formData.append("email", values?.email || "");
            formData.append("role_id", values?.role || "");
            // One or more designations ("user_type[]" -> an array on the server, which keeps a single one as a plain string).
            toDesignationList(values?.user_type).forEach((d) => formData.append("user_type[]", d));
            formData.append("status", (values?.status || "").toLowerCase());
            formData.append("location", values?.location || "");
            formData.append("two_step_enabled", JSON.parse(values?.two_step_enabled));
            formData.append("is_meeting_am", values?.is_meeting_am ? "true" : "false");
            if (values.newPassword) {
                formData.append("password", values.newPassword);
            }
            if (values.confirmPassword) {
                formData.append("confirm_password", values.confirmPassword);
            }
            if (Array.isArray(values?.employeePortfolio)) {
                values.employeePortfolio.forEach((id) => {
                    formData.append("employee_portfolio[]", id);
                });
                // FormData cannot carry an empty array: with everyone unselected nothing would be sent and the old
                // portfolio would stay in the DB. A blank entry (ignored by the server) says the list is now empty.
                if (selectedUser && values.employeePortfolio.length === 0) {
                    formData.append("employee_portfolio[]", "");
                }
            }
            if (values?.avatar instanceof File) {
                formData.append("profile_pic", values.avatar);
            }
            if (selectedUser) {
                const res = await ApiCall("PUT", `/api/users/${selectedUser.id}`, formData, { Authorization: token });
                if (res && 'data' in res && res?.data?.success) {
                    toast.success(res.data?.message || "User updated successfully");
                    setDrawerOpen(false);
                    setSelectedUser(null);
                    fetchUsers();
                    return true;
                }
                else {
                    const errorRes = res;
                    toast.error(errorRes?.response?.data?.message || "Update failed");
                    return false;
                }
            }
            else {
                const res = await ApiCall("POST", "/api/users", formData, { Authorization: token });
                if (res && 'data' in res && res?.data?.success) {
                    toast.success(res.data?.message || "User created successfully");
                    setDrawerOpen(false);
                    fetchUsers();
                    return true;
                }
                else {
                    const errorRes = res;
                    toast.error(errorRes?.response?.data?.message || "Create failed");
                    return false;
                }
            }
        }
        catch (error) {
            toast.error("An unexpected error occurred. Please try again.");
            return false;
        }
    };
    const columns = [
        {
            id: "userName",
            label: "User Name",
            width: "305px",
            align: "left",
            sortable: true,
        },
        {
            id: "email",
            label: "Email",
            width: "305px",
            align: "left",
            sortable: true,
        },
        {
            id: "role",
            label: "Role",
            width: "305px",
            align: "left",
            sortable: true,
        },
        {
            id: "designation",
            label: "Designation",
            width: "180px",
            align: "left",
            sortable: false,
            showColumnActions: false,
        },
        {
            id: "actions",
            label: "Actions",
            width: "128px",
            align: "center",
            sortable: false,
            draggable: false,
            showColumnActions: false,
        },
    ];
    const renderCell = (row, column) => {
        const rowIndex = rows.findIndex((u) => u.id === row.id);
        const displayIndex = (currentPage - 1) * itemsPerPage + rowIndex + 1;
        const roleClass = roleClassSequence[rowIndex % roleClassSequence.length];
        switch (column.id) {
            case "index":
                return (_jsx(Typography, { className: "user-table-index-text", children: displayIndex }));
            case "userName":
                return (_jsxs(Box, { className: "user-table-name-cell", children: [_jsx(Avatar, { className: "user-table-avatar", src: row.profilePic || undefined, children: row.initials }), _jsx(Box, { className: "user-table-name-wrapper", children: _jsx(Typography, { className: "user-table-name-text", children: row.name }) })] }));
            case "email":
                return (_jsx(Typography, { className: "user-table-email-text", children: row.email }));
            case "role":
                return (_jsxs(Box, { className: `user-table-role-badge ${roleClass}`, sx: { display: "flex", alignItems: "center", gap: "4px" }, children: [_jsx(Typography, { className: "user-table-role-text", children: row.roleName || "" }), row.hasOverrides && (_jsx("div", { className: "d-flex", children: _jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M3 1.5v6m0 0A1.5 1.5 0 1 0 4.5 9M3 7.5A1.5 1.5 0 0 1 4.5 9M9 4.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m0 0A4.5 4.5 0 0 1 4.5 9", stroke: "#2e90fa", "stroke-width": "1.5", "stroke-linecap": "round", "stroke-linejoin": "round" }) }) }))] }));
            case "designation": {
                // One chip per designation (a user can hold several): the first 2, the rest folded into "+N".
                const designations = toDesignationList(row.raw?.user_type);
                if (designations.length === 0)
                    return _jsx(Typography, { className: "user-table-email-text", children: "-" });
                const shown = designations.slice(0, 2);
                const extra = designations.slice(2);
                const chipSx = { width: "auto", minWidth: "28px", padding: "0 8px", borderRadius: "14px" };
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }, children: [shown.map((designation) => (_jsx(CommonChip, { label: designationInitials(designation), tooltip: designation, renderType: "circle", index: designationColorIndex(designation), sx: chipSx }, designation))), extra.length > 0 && (_jsx(CommonChip, { label: `+${extra.length}`, tooltip: extra.join(", "), renderType: "circle", index: 0, sx: { ...chipSx, background: "#F2F4F7", color: "#344054" } }))] }));
            }
            case "actions":
                const currentUserId = getCurrentUserId();
                const isSelf = currentUserId && currentUserId === row.id;
                return (_jsx(Box, { className: "user-table-actions-cell", children: hasPermission &&
                        _jsxs(_Fragment, { children: [editLoadingUserId === row.id ? (_jsx(CircularProgress, { color: "primary", size: 18 })) : (_jsx(HoverTooltip, { title: "Edit User", children: _jsx(IconButton, { className: "user-table-action-button", onClick: () => handleEdit(row), size: "small", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M14.167 2.5A2.357 2.357 0 0 1 17.5 5.833L6.25 17.083l-4.583 1.25 1.25-4.583z", stroke: "#52525b", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }) })), invitationLoadingUserId === row.id ? (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34 }, children: _jsx(CircularProgress, { size: 16 }) })) : (_jsx(HoverTooltip, { title: row.isInvitationSent ? "Invitation already sent — resend now." : "Send Invitation", children: _jsx(IconButton, { className: "user-table-action-button", onClick: () => handleSendInvitation(row), size: "small", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M22 2L11 13", stroke: row.isInvitationSent ? "#EE2A7B" : "#52525b", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M22 2L15 22L11 13L2 9L22 2Z", stroke: row.isInvitationSent ? "#EE2A7B" : "#52525b", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }) }) })), !isSelf && (_jsx(HoverTooltip, { title: "Delete User", children: _jsx(IconButton, { className: "user-table-action-button user-delete-btn", onClick: () => handleDelete(row), size: "small", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M2.5 5h1.667m0 0H17.5M4.167 5v11.667a1.667 1.667 0 0 0 1.666 1.667h8.334a1.667 1.667 0 0 0 1.666-1.667V5zm2.5 0V3.334a1.667 1.667 0 0 1 1.666-1.667h3.334a1.667 1.667 0 0 1 1.666 1.667V5m-5 4.167v5m3.334-5v5", stroke: "#535862", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }) }))] }) }));
            default:
                return null;
        }
    };
    const columnFilterConfig = useMemo(() => ({
        enable: true,
        columns: {
            userName: {
                type: "search",
            },
            email: {
                type: "search",
            },
            role: {
                type: "dropdown",
                options: roleOptions,
            },
        },
    }), [roleOptions]);
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "user-list-container", children: _jsxs(Paper, { className: "user-list-card", elevation: 0, children: [_jsx(Box, { className: "user-list-header", children: _jsxs(Box, { className: "user-list-header-content", children: [_jsx(CommonTabs, { selectedValue: activeTab, onChange: (val) => { setActiveTab(val); setCurrentPage(1); }, tabs: [
                                            {
                                                value: "active",
                                                label: (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Box, { className: "user-list-tab-dot user-list-tab-dot-active", sx: { margin: 0 } }), "Active (", activeCount, " User", activeCount !== 1 ? "s" : "", ")"] }))
                                            },
                                            {
                                                value: "inactive",
                                                label: (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Box, { className: "user-list-tab-dot user-list-tab-dot-inactive", sx: { margin: 0 } }), "Inactive (", inactiveCount, " User", inactiveCount !== 1 ? "s" : "", ")"] }))
                                            }
                                        ] }), _jsxs(Box, { className: "user-list-toolbar-actions", children: [_jsx(TableToolbar, { title: "", showSearch: true, showRows: true, showExpand: true, showFullScreen: true, onSearchChange: handleSearchChange, searchValue: searchText, enableColumnVisibility: true, onColumnVisibilityClick: handleColumnVisibilityClick, enableColumnFilters: true, showFilterRow: showFilterRow, onFilterRowToggle: () => {
                                                    setShowFilterRow((prev) => {
                                                        const next = !prev;
                                                        // 👇 sirf jab user toolbar icon se band kare
                                                        if (!next) {
                                                            setActiveFilterColumnId(null);
                                                        }
                                                        return next;
                                                    });
                                                } }), hasPermission &&
                                                _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", onClick: handleAddNewUser, startIcon: _jsx(AddIcon, {}), children: "Add New User" })] })] }) }), _jsx(Box, { className: "user-list-table-wrapper", children: _jsx(CommonTable, { columns: columns, rows: rows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: handleSortChange, expandTrigger: "none", type: 'userlist-table', isLoading: loading, isFirstLoad: isFirstLoad.current, enableSelection: false, selectedRows: selectedRows, onSelectionChange: handleSelectionChange, columnVisibilityConfig: {
                                    enable: true,
                                    columns: ["userName", "email", "role", "designation", "actions"],
                                    enableReorder: true,
                                }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: handleColumnVisibilityClose, enableColumnActions: true, enableColumnFilters: true, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, onFilterRowToggle: handleFilterRowToggle, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: handleColumnPinningChange, columnFilterConfig: columnFilterConfig }) }), totalPages > 1 && (_jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: handlePageChange, maxVisiblePages: 5, totalEntries: activeTab === "active" ? activeCount : inactiveCount, itemsPerPage: itemsPerPage, showEntriesMessage: true }) }))] }) }), drawerOpen && _jsx(UserDrawer, { open: drawerOpen, onClose: handleDrawerClose, userData: selectedUser, onSave: handleSaveUser, roleOptions: roleOptions, employeePortfolioOptions: portfolioOptionsForDrawer, designationOptions: designationOptions, onOverrideSave: handleOverrideSave }), deleteState?.open && _jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleteState.loading, title: "Are You Sure?", description: "You\u2019re about to remove this record, which is connected to multiple items. Once deleted, this action cannot be undone.", confirmText: "Yes, Proceed", cancelText: "No, Cancel", onClose: () => !deleteState.loading &&
                    setDeleteState({ open: false, loading: false, payload: null }), onConfirm: confirmDelete })] }));
}
